import { useState, useEffect, useRef } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { AlertTriangle, PieChart, Plus, RotateCcw, Trash2 } from "lucide-react";
import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import {
  STATISTICS_FIELDS,
  getOperatorsForField,
  getFieldOptionConfig,
  getValueEditorType,
  useFieldOptionConfig,
} from "@/constants/segmentOptions";
import { useApp } from "@/contexts/AppContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const LABEL_CLS =
  "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.04em] text-(--text-muted)";
const CONTROL_CLS =
  "h-10 w-full rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 text-[13px] text-(--text-primary) transition-colors focus:border-(--brand-primary) focus:outline-none";

const centerTextPlugin = {
  id: "centerText",
  beforeDraw: (chart) => {
    const { width, height, ctx } = chart;
    ctx.restore();

    const fontSize = Math.min(width, height) / 20;
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#333";

    const text = `${chart.config.options.plugins.centerText.filteredTotal}`;
    const subText = `of ${chart.config.options.plugins.centerText.totalVasts} total`;

    const textX = Math.round((width - ctx.measureText(text).width) / 2);
    const textY = height / 2 - fontSize / 2;

    ctx.fillText(text, textX, textY);

    ctx.font = `${fontSize * 0.6}px Arial`;
    ctx.fillStyle = "#666";
    const subTextX = Math.round((width - ctx.measureText(subText).width) / 2);
    const subTextY = textY + fontSize * 0.8;

    ctx.fillText(subText, subTextX, subTextY);
    ctx.save();
  },
};

export default function VastsDistributionChart() {
  const { performRequest } = useApp();
  const fieldOptionConfig = useFieldOptionConfig();

  const [mode, setMode] = useState("field");

  const [selectedField, setSelectedField] = useState("country");

  const [filters, setFilters] = useState([
    { id: 1, field: "country", operator: "eq", value: "" },
  ]);

  const [distribution, setDistribution] = useState([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [totalVasts, setTotalVasts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const nextFilterId = useRef(2);
  const requestIdRef = useRef(0);

  const addFilter = () => {
    setFilters((prev) => [
      ...prev,
      {
        id: nextFilterId.current++,
        field: "country",
        operator: "eq",
        value: "",
      },
    ]);
  };

  const removeFilter = (filterId) => {
    if (filters.length > 1) {
      setFilters((prev) => prev.filter((f) => f.id !== filterId));
    } else {
      setFilters([{ id: 1, field: "country", operator: "eq", value: "" }]);
    }
  };

  const updateFilter = (filterId, updates) => {
    setFilters((prev) =>
      prev.map((filter) =>
        filter.id === filterId ? { ...filter, ...updates } : filter,
      ),
    );
  };

  const fetchFieldDistribution = async () => {
    if (!selectedField) return;

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        field: selectedField,
      });

      const response = await performRequest(
        `/statistics/field-distribution?${params}`,
      );

      if (currentRequestId === requestIdRef.current) {
        setDistribution(response.distribution || []);
        setTotalVasts(response.totalVasts || 0);
        setFilteredTotal(response.totalVasts || 0);
        setLoading(false);
      }
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        console.error("Error fetching field distribution:", err);
        setError(err.message || "Failed to fetch field distribution data");
        setDistribution([]);
        setFilteredTotal(0);
        setTotalVasts(0);
        setLoading(false);
      }
    }
  };

  const fetchFilterDistribution = async () => {
    if (filters.length === 0 || !filters[0].field) return;

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const apiFilters = filters
        .filter(
          (f) =>
            f.field &&
            f.value !== "" &&
            f.value !== null &&
            f.value !== undefined,
        )
        .map((f) => ({
          field: f.field,
          operator: f.operator || "eq",
          value: f.value,
        }));

      if (apiFilters.length === 0) {
        if (currentRequestId === requestIdRef.current) {
          setDistribution([]);
          setFilteredTotal(0);
          setTotalVasts(0);
          setLoading(false);
        }
        return;
      }

      const params = new URLSearchParams({
        filters: JSON.stringify(apiFilters),
      });

      const response = await performRequest(
        `/statistics/vasts-distribution?${params}`,
      );

      if (currentRequestId === requestIdRef.current) {
        setDistribution(response.distribution || []);
        setFilteredTotal(response.filteredTotal || 0);
        setTotalVasts(response.totalVasts || 0);
        setLoading(false);
      }
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        console.error("Error fetching filter distribution:", err);
        setError(err.message || "Failed to fetch filter distribution data");
        setDistribution([]);
        setFilteredTotal(0);
        setTotalVasts(0);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (mode === "field") {
      fetchFieldDistribution();
    } else {
      fetchFilterDistribution();
    }
  }, [mode, selectedField, filters]);

  const renderValueEditor = (filter) => {
    const field = STATISTICS_FIELDS.find((f) => f.value === filter.field);
    const operators = getOperatorsForField(filter.field);
    const selectedOperator = operators.find(
      (op) => op.value === filter.operator,
    );
    const valueType = getValueEditorType(selectedOperator);
    const fieldConfig = getFieldOptionConfig(filter.field, fieldOptionConfig);

    if (valueType === "none") {
      return null;
    }

    if (valueType === "multi") {
      if (fieldConfig?.options) {
        return (
          <select
            multiple
            value={Array.isArray(filter.value) ? filter.value : []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, (option) => {
                const rawValue = fieldConfig.toRaw
                  ? fieldConfig.toRaw(option.value)
                  : option.value;
                return rawValue;
              });
              updateFilter(filter.id, { value: values });
            }}
            className={`${CONTROL_CLS} h-auto min-h-[96px] py-2`}
          >
            {fieldConfig.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {option.kind ? ` [${option.kind}]` : ""}
              </option>
            ))}
          </select>
        );
      } else {
        return (
          <input
            type="text"
            value={Array.isArray(filter.value) ? filter.value.join(", ") : ""}
            onChange={(e) => {
              const values = e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean);
              updateFilter(filter.id, { value: values });
            }}
            placeholder="Enter comma-separated values"
            className={CONTROL_CLS}
          />
        );
      }
    }

    if (valueType === "range") {
      const rangeValue = Array.isArray(filter.value) ? filter.value : ["", ""];
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={rangeValue[0] || ""}
            onChange={(e) => {
              const newRange = [e.target.value, rangeValue[1]];
              updateFilter(filter.id, { value: newRange });
            }}
            placeholder="Min"
            className={CONTROL_CLS}
          />
          <span className="text-[12px] text-(--text-muted)">to</span>
          <input
            type="number"
            value={rangeValue[1] || ""}
            onChange={(e) => {
              const newRange = [rangeValue[0], e.target.value];
              updateFilter(filter.id, { value: newRange });
            }}
            placeholder="Max"
            className={CONTROL_CLS}
          />
        </div>
      );
    }

    if (field?.type === "boolean") {
      return (
        <select
          value={filter.value}
          onChange={(e) =>
            updateFilter(filter.id, { value: e.target.value === "true" })
          }
          className={CONTROL_CLS}
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (field?.type === "datetime") {
      return (
        <input
          type="date"
          value={filter.value || ""}
          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
          className={CONTROL_CLS}
        />
      );
    }

    if (fieldConfig?.options) {
      return (
        <select
          value={
            fieldConfig.toString
              ? fieldConfig.toString(filter.value)
              : filter.value
          }
          onChange={(e) => {
            const rawValue = fieldConfig.toRaw
              ? fieldConfig.toRaw(e.target.value)
              : e.target.value;
            updateFilter(filter.id, { value: rawValue });
          }}
          className={CONTROL_CLS}
        >
          <option value="">Select...</option>
          {fieldConfig.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {option.kind ? ` [${option.kind}]` : ""}
            </option>
          ))}
        </select>
      );
    }

    const inputType = field?.type === "number" ? "number" : "text";
    return (
      <input
        type={inputType}
        value={filter.value || ""}
        onChange={(e) => {
          const value =
            field?.type === "number" ? Number(e.target.value) : e.target.value;
          updateFilter(filter.id, { value });
        }}
        className={CONTROL_CLS}
        placeholder={`Enter ${field?.label || "value"}`}
      />
    );
  };

  const chartData = {
    labels: distribution.map((item) => item.label),
    datasets: [
      {
        data: distribution.map((item) => item.value),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
          "#E74C3C",
          "#2ECC71",
          "#F39C12",
          "#8E44AD",
          "#3498DB",
          "#E67E22",
        ],
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 40,
        left: 20,
        right: 20,
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 25,
          usePointStyle: true,
          font: {
            size: 12,
          },
          boxWidth: 15,
          boxHeight: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const percentage =
              totalVasts > 0
                ? ((context.parsed / totalVasts) * 100).toFixed(1)
                : 0;
            const filteredPercentage =
              filteredTotal > 0
                ? ((context.parsed / filteredTotal) * 100).toFixed(1)
                : 0;
            return [
              `${context.label}: ${context.parsed}`,
              `${percentage}% of total VASTs (${totalVasts})`,
              `${filteredPercentage}% of filtered results (${filteredTotal})`,
            ];
          },
        },
      },
      centerText: {
        filteredTotal,
        totalVasts,
      },
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: "#fff",
      },
    },
  };

  const tabs = [
    {
      id: "field",
      label: "Field distribution",
      description: "View distribution of all VASTs by a selected field",
    },
    {
      id: "filter",
      label: "Filter analysis",
      description: "Analyze VASTs using multiple filters and conditions",
    },
  ];
  const activeTab = tabs.find((t) => t.id === mode) ?? tabs[0];

  return (
    <div>
      <nav className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-(--border-subtle)">
        {tabs.map((tab) => {
          const isActive = mode === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMode(tab.id)}
              className={`relative flex items-center gap-2 whitespace-nowrap border-0 bg-transparent px-4 py-[11px] text-[13.5px] transition-colors ${
                isActive
                  ? "font-bold text-(--brand-primary)"
                  : "font-semibold text-(--text-secondary) hover:text-(--text-primary)"
              }`}
            >
              {tab.label}
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-[2.5px] rounded-[2px] bg-(--brand-primary)"
                />
              )}
            </button>
          );
        })}
      </nav>
      <p className="mb-5 text-[12.5px] text-(--text-muted)">
        {activeTab.description}
      </p>
      <div>
        {mode === "field" && (
          <Card className="border border-(--border-subtle)">
            <div className="max-w-sm">
              <label htmlFor="distribution-field" className={LABEL_CLS}>
                Distribution field
              </label>
              <select
                id="distribution-field"
                value={selectedField}
                onChange={(e) => setSelectedField(e.target.value)}
                className={CONTROL_CLS}
              >
                {STATISTICS_FIELDS.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>
          </Card>
        )}

        {/* Filter Analysis Mode */}
        {mode === "filter" && (
          <section>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-(--text-primary)">
                Filters
              </h3>
              <Button variant="secondary" onClick={addFilter}>
                <Plus size={15} strokeWidth={2.5} />
                Add filter
              </Button>
            </div>

            <div className="flex flex-col">
              {filters.map((filter, index) => (
                <div key={filter.id}>
                  <Card className="border border-(--border-subtle)">
                    <div className="grid items-end gap-4 md:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_minmax(220px,1.4fr)_auto]">
                      <div>
                        <label
                          htmlFor={`filter-field-${filter.id}`}
                          className={LABEL_CLS}
                        >
                          Field
                        </label>
                        <select
                          id={`filter-field-${filter.id}`}
                          value={filter.field}
                          onChange={(e) => {
                            const newField = e.target.value;
                            const operators = getOperatorsForField(newField);
                            updateFilter(filter.id, {
                              field: newField,
                              operator: operators[0]?.value || "eq",
                              value: "",
                            });
                          }}
                          className={CONTROL_CLS}
                        >
                          {STATISTICS_FIELDS.map((field) => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor={`filter-operator-${filter.id}`}
                          className={LABEL_CLS}
                        >
                          Operator
                        </label>
                        <select
                          id={`filter-operator-${filter.id}`}
                          value={filter.operator}
                          onChange={(e) =>
                            updateFilter(filter.id, {
                              operator: e.target.value,
                              value: "",
                            })
                          }
                          className={CONTROL_CLS}
                        >
                          {getOperatorsForField(filter.field).map((op) => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={LABEL_CLS}>Value</label>
                        {renderValueEditor(filter)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFilter(filter.id)}
                        title={
                          filters.length > 1 ? "Remove filter" : "Reset filter"
                        }
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) px-4 text-sm font-semibold text-(--status-danger) transition-colors hover:bg-(--surface-2)"
                      >
                        {filters.length > 1 ? (
                          <>
                            <Trash2 size={15} strokeWidth={2} />
                            Delete
                          </>
                        ) : (
                          <>
                            <RotateCcw size={15} strokeWidth={2} />
                            Reset
                          </>
                        )}
                      </button>
                    </div>
                  </Card>
                  {index < filters.length - 1 && (
                    <div className="flex justify-center py-2">
                      <span className="rounded-(--radius-pill) bg-(--pastel-violet) px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-(--brand-primary)">
                        and
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {error && (
          <div className="mt-5 flex items-center gap-3 rounded-(--radius-md) border border-(--status-danger)/30 bg-(--pastel-rose) px-4 py-3 text-[13px] text-(--status-danger)">
            <AlertTriangle size={16} strokeWidth={2} />
            <p>
              <strong>Error:</strong> {error}
            </p>
          </div>
        )}

        {loading && (
          <div className="mt-10 flex flex-col items-center gap-3 text-(--text-secondary)">
            <span className="spinner" aria-hidden="true" />
            <p className="text-[13px]">Loading chart data…</p>
          </div>
        )}

        {!loading && !error && distribution.length > 0 && (
          <div
            className={`mt-6 grid items-start gap-6 ${
              mode === "field" ? "lg:grid-cols-[1.2fr_1fr]" : ""
            }`}
          >
            {mode === "field" && (
              <Card className="border border-(--border-subtle)">
                <div className="relative h-[420px]">
                  <Pie
                    data={chartData}
                    options={chartOptions}
                    plugins={[centerTextPlugin]}
                  />
                </div>
              </Card>
            )}
            <Card className="flex flex-col gap-4 border border-(--border-subtle)">
              <h3 className="text-base font-semibold text-(--text-primary)">
                {mode === "field"
                  ? "Field distribution summary"
                  : "Filter analysis results"}
              </h3>
              {mode === "filter" && (
                <p className="text-[13px] text-(--text-secondary)">
                  Results showing VASTs matching your filter criteria. Match
                  rate:{" "}
                  <span className="font-semibold text-(--text-primary)">
                    {totalVasts > 0
                      ? ((filteredTotal / totalVasts) * 100).toFixed(1)
                      : 0}
                    %
                  </span>
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {mode === "field" ? (
                  <>
                    <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
                      <p className={LABEL_CLS}>Selected field</p>
                      <p className="text-[15px] font-bold text-(--text-primary)">
                        {STATISTICS_FIELDS.find(
                          (f) => f.value === selectedField,
                        )?.label || selectedField}
                      </p>
                    </div>
                    <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
                      <p className={LABEL_CLS}>Total VASTs</p>
                      <p className="text-[15px] font-bold text-(--text-primary)">
                        {totalVasts.toLocaleString("en-US")}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
                      <p className={LABEL_CLS}>Filtered results</p>
                      <p className="text-[15px] font-bold text-(--text-primary)">
                        {filteredTotal.toLocaleString("en-US")}
                      </p>
                    </div>
                    <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
                      <p className={LABEL_CLS}>Total VASTs</p>
                      <p className="text-[15px] font-bold text-(--text-primary)">
                        {totalVasts.toLocaleString("en-US")}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {mode === "field" && (
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead>
                      <tr className="text-left text-[12px] font-bold text-(--text-muted)">
                        <th className="border-b border-(--border-subtle) px-3 pb-2">
                          Value
                        </th>
                        <th className="border-b border-(--border-subtle) px-3 pb-2 text-right">
                          Count
                        </th>
                        <th className="border-b border-(--border-subtle) px-3 pb-2 text-right">
                          % of total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {distribution.map((item, index) => {
                        const totalPercentage =
                          totalVasts > 0
                            ? ((item.value / totalVasts) * 100).toFixed(1)
                            : 0;
                        return (
                          <tr
                            key={index}
                            className="border-b border-(--border-subtle)"
                          >
                            <td
                              className="max-w-[220px] truncate px-3 py-2 text-(--text-primary)"
                              title={item.label}
                            >
                              {item.label}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-(--text-primary) tabular-nums">
                              {item.value.toLocaleString("en-US")}
                            </td>
                            <td className="px-3 py-2 text-right text-(--text-secondary) tabular-nums">
                              {totalPercentage}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {!loading && !error && distribution.length === 0 && (
          <div className="mt-12 flex flex-col items-center gap-3 text-(--text-muted)">
            <PieChart size={28} strokeWidth={1.5} aria-hidden="true" />
            <p className="text-[13px]">
              No data found for the selected filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
